'use client';

import { useState, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { PrimaryButton, SecondaryButton } from '@/components/platform/buttons';
import Alert from '@/components/platform/ui/Alert';
import SocialPlatformCard from './SocialPlatformCard';
import { getPlatformById } from '@/lib/products/valuations/social-deepdive/platforms';
import { calculateTotals } from '@/lib/products/valuations/social-deepdive/calculations';
import type {
  PlatformMetrics,
  TotalMetrics,
} from '@/lib/products/valuations/social-deepdive/types';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';
import { logger } from '@/lib/platform/utils/logger';
import { queryKeys } from '@/lib/platform/hooks/queries/query-keys';

interface SocialDeepDiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (totals: TotalMetrics, platformMetrics: Record<string, PlatformMetrics>) => void;
  valuationId: string | null;
  organizationId: string;
  existingPlatformMetrics?: Record<string, PlatformMetrics>;
  currentTotals: TotalMetrics; // MASTER data from valuations table (same as button displays)
  numberFormat?: NumberFormatType;
}

export default function SocialDeepDiveModal({
  isOpen,
  onClose,
  onSave,
  valuationId,
  organizationId,
  existingPlatformMetrics,
  currentTotals: masterTotals,
  numberFormat = 'eu',
}: SocialDeepDiveModalProps) {
  const queryClient = useQueryClient();
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Platform metrics state (keyed by platform ID)
  const [platformMetrics, setPlatformMetrics] = useState<Record<string, PlatformMetrics>>({});

  // Enabled platforms for this organization
  const [enabledPlatformIds, setEnabledPlatformIds] = useState<string[]>([]);

  // Allocated totals from valuation (for migration/transition UX)
  const [allocatedTotals, setAllocatedTotals] = useState<{
    followers: number;
    followers_cpm: number;
    impressions: number;
    impressions_cpm: number;
    engagements: number;
    engagements_cpm: number;
  } | null>(null);

  // Confirmation modal for clearing allocated values
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [metricToClear, setMetricToClear] = useState<
    'followers' | 'impressions' | 'engagements' | null
  >(null);

  // Load data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch organization's enabled platforms AND existing valuation data
        const url = valuationId
          ? `/api/valuations/social-platforms?valuationId=${valuationId}&organizationId=${organizationId}`
          : `/api/settings/social-platforms?organizationId=${organizationId}`;

        const response = await fetchWithBypass(url);

        if (!response.ok) {
          throw new Error('Failed to load platform data');
        }

        const data = await response.json();

        if (valuationId) {
          // Editing existing valuation - load platform data
          setEnabledPlatformIds(data.enabled_platforms || []);
          setAllocatedTotals(data.allocated_totals || null);

          // Convert array to keyed object
          const metricsMap: Record<string, PlatformMetrics> = {};
          for (const platformId of data.enabled_platforms || []) {
            const existingMetrics = data.platforms?.find(
              (p: PlatformMetrics) => p.platform === platformId
            );
            metricsMap[platformId] = existingMetrics || {
              platform: platformId,
              followers: 0,
              followers_cpm: 0,
              impressions: 0,
              impressions_cpm: 0,
              engagements: 0,
              engagements_cpm: 0,
            };
          }
          setPlatformMetrics(metricsMap);
        } else {
          // New valuation - use existing metrics if available, otherwise initialize empty
          setEnabledPlatformIds(data.enabled_platforms || []);

          const metricsMap: Record<string, PlatformMetrics> = {};
          for (const platformId of data.enabled_platforms || []) {
            // Use existing metrics if available (from previous modal session)
            metricsMap[platformId] = existingPlatformMetrics?.[platformId] || {
              platform: platformId,
              followers: 0,
              followers_cpm: 0,
              impressions: 0,
              impressions_cpm: 0,
              engagements: 0,
              engagements_cpm: 0,
            };
          }
          setPlatformMetrics(metricsMap);
        }
      } catch (err: unknown) {
        logger.error('Error loading social platform data', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load platform data';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, valuationId, organizationId, existingPlatformMetrics]);

  function handlePlatformChange(platformId: string, metrics: PlatformMetrics) {
    setPlatformMetrics((prev) => ({
      ...prev,
      [platformId]: metrics,
    }));
  }

  function requestClearAllocated(metric: 'followers' | 'impressions' | 'engagements') {
    setMetricToClear(metric);
    setShowClearConfirmation(true);
  }

  async function confirmClearAllocated() {
    if (!valuationId || !allocatedTotals || !metricToClear) return;

    try {
      // Clear the specific allocated value in the database
      const updateData: Record<string, number> = {};
      if (metricToClear === 'followers') {
        updateData.social_followers = 0;
        updateData.social_followers_cpm = 0;
      } else if (metricToClear === 'impressions') {
        updateData.social_impressions = 0;
        updateData.social_impressions_cpm = 0;
      } else if (metricToClear === 'engagements') {
        updateData.social_engagements = 0;
        updateData.social_cpm = 0;
      }

      const response = await fetchWithBypass('/api/products/valuations/valuations', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: valuationId,
          ...updateData,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to clear allocated value');
      }

      // Update local state
      setAllocatedTotals((prev) => {
        if (!prev) return null;
        const updated = { ...prev };
        if (metricToClear === 'followers') {
          updated.followers = 0;
          updated.followers_cpm = 0;
        } else if (metricToClear === 'impressions') {
          updated.impressions = 0;
          updated.impressions_cpm = 0;
        } else if (metricToClear === 'engagements') {
          updated.engagements = 0;
          updated.engagements_cpm = 0;
        }
        return updated;
      });

      // Close confirmation modal
      setShowClearConfirmation(false);
      setMetricToClear(null);
    } catch (err: unknown) {
      logger.error('Error clearing allocated value', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear allocated value';
      setError(errorMessage);
      setShowClearConfirmation(false);
      setMetricToClear(null);
    }
  }

  function cancelClearAllocated() {
    setShowClearConfirmation(false);
    setMetricToClear(null);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);

    try {
      // Convert metrics map to array
      const platformsArray = Object.values(platformMetrics);

      // Calculate totals from platform data
      const totals = calculateTotals(platformsArray);

      // Save to database if we have a valuation ID
      if (valuationId) {
        // 1. Save platform breakdown to valuation_social_platforms
        const platformResponse = await fetchWithBypass('/api/valuations/social-platforms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            valuationId,
            organizationId,
            platforms: platformsArray,
          }),
        });

        if (!platformResponse.ok) {
          throw new Error('Failed to save platform data');
        }

        // 2. Save calculated totals to valuations table (MASTER data)
        const valuationResponse = await fetchWithBypass('/api/products/valuations/valuations', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: valuationId,
            social_followers: totals.followers,
            social_followers_cpm: totals.followers_cpm,
            social_impressions: totals.impressions,
            social_impressions_cpm: totals.impressions_cpm,
            social_engagements: totals.engagements,
            social_cpm: totals.engagements_cpm,
          }),
        });

        if (!valuationResponse.ok) {
          throw new Error('Failed to update valuation totals');
        }

        // Invalidate React Query cache to force fresh data load
        queryClient.invalidateQueries({ queryKey: queryKeys.valuations.detail(valuationId) });
        queryClient.invalidateQueries({
          queryKey: queryKeys.valuations.list({ organizationId: organizationId }),
        });
      }

      // Update parent form state (so UI reflects changes immediately)
      onSave(totals, platformMetrics);
      onClose();
    } catch (err: unknown) {
      logger.error('Error saving social platform data', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save platform data';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  }

  // Calculate platform breakdown totals (for "allocated" comparison)
  const platformTotals = calculateTotals(Object.values(platformMetrics));

  // Use masterTotals for display (same data as button shows - from valuations table)
  const currentTotals = masterTotals;

  const currencySymbol = getCurrencySymbol(baseCurrency);

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} className="relative z-50">
        <DialogBackdrop
          transition
          className={`fixed inset-0 backdrop-blur-[2px] transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in ${
            darkMode ? 'bg-gray-900/75' : 'bg-gray-500/75'
          }`}
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 text-center sm:p-4">
            <DialogPanel
              transition
              className={`relative flex max-h-[90vh] w-full transform flex-col overflow-hidden rounded-lg text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in sm:my-8 sm:max-w-4xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 md:max-h-none ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4">
                <DialogTitle
                  className={`text-lg font-semibold ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  Social Media Platform Breakdown
                </DialogTitle>
                <button
                  type="button"
                  onClick={onClose}
                  className={`rounded-md p-1 transition-colors hover:bg-white/10 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {error && (
                  <div className="mb-4 px-6">
                    <Alert type="error" message={error} onDismiss={() => setError(null)} />
                  </div>
                )}

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
                  </div>
                ) : (
                  <>
                    {/* Totals Summary - Sticky, Full Width */}
                    <div
                      className={`sticky top-0 z-10 mb-6 p-6 ${
                        darkMode ? 'bg-gray-900' : 'bg-white'
                      }`}
                    >
                      <h3
                        className={`mb-3 text-sm font-semibold ${
                          darkMode ? 'text-gray-100' : 'text-gray-900'
                        }`}
                      >
                        Totals Across All Platforms
                      </h3>
                      <div className="grid grid-cols-3 gap-3">
                        {/* Followers */}
                        <div>
                          <p
                            className={`mb-1 text-xs ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            Total Followers
                          </p>
                          <div
                            className={`text-lg font-semibold ${
                              darkMode ? 'text-gray-100' : 'text-gray-900'
                            }`}
                          >
                            {platformTotals &&
                            platformTotals.followers > 0 &&
                            currentTotals.followers !== platformTotals.followers ? (
                              <div className="text-sm">
                                <div>
                                  {platformTotals.followers.toLocaleString()} /{' '}
                                  {currentTotals.followers.toLocaleString()}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span
                                    className={
                                      platformTotals.followers < currentTotals.followers
                                        ? 'text-yellow-500'
                                        : 'text-red-500'
                                    }
                                  >
                                    (
                                    {Math.abs(
                                      currentTotals.followers - platformTotals.followers
                                    ).toLocaleString()}{' '}
                                    {platformTotals.followers < currentTotals.followers
                                      ? 'allocated'
                                      : 'over'}
                                    )
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => requestClearAllocated('followers')}
                                    className={`rounded p-0.5 transition-colors hover:bg-white/10 ${
                                      darkMode
                                        ? 'text-gray-400 hover:text-gray-300'
                                        : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                    title="Clear allocated followers"
                                  >
                                    <XCircleIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              currentTotals.followers.toLocaleString()
                            )}
                          </div>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg CPM: {currencySymbol}
                            {platformTotals.followers_cpm.toFixed(4)}
                          </p>
                        </div>

                        {/* Impressions */}
                        <div>
                          <p
                            className={`mb-1 text-xs ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            Total Impressions
                          </p>
                          <div
                            className={`text-lg font-semibold ${
                              darkMode ? 'text-gray-100' : 'text-gray-900'
                            }`}
                          >
                            {platformTotals &&
                            platformTotals.impressions > 0 &&
                            currentTotals.impressions !== platformTotals.impressions ? (
                              <div className="text-sm">
                                <div>
                                  {platformTotals.impressions.toLocaleString()} /{' '}
                                  {currentTotals.impressions.toLocaleString()}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span
                                    className={
                                      platformTotals.impressions < currentTotals.impressions
                                        ? 'text-yellow-500'
                                        : 'text-red-500'
                                    }
                                  >
                                    (
                                    {Math.abs(
                                      currentTotals.impressions - platformTotals.impressions
                                    ).toLocaleString()}{' '}
                                    {platformTotals.impressions < currentTotals.impressions
                                      ? 'allocated'
                                      : 'over'}
                                    )
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => requestClearAllocated('impressions')}
                                    className={`rounded p-0.5 transition-colors hover:bg-white/10 ${
                                      darkMode
                                        ? 'text-gray-400 hover:text-gray-300'
                                        : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                    title="Clear allocated impressions"
                                  >
                                    <XCircleIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              currentTotals.impressions.toLocaleString()
                            )}
                          </div>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg CPM: {currencySymbol}
                            {platformTotals.impressions_cpm.toFixed(4)}
                          </p>
                        </div>

                        {/* Engagements */}
                        <div>
                          <p
                            className={`mb-1 text-xs ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            Total Engagements
                          </p>
                          <div
                            className={`text-lg font-semibold ${
                              darkMode ? 'text-gray-100' : 'text-gray-900'
                            }`}
                          >
                            {platformTotals &&
                            platformTotals.engagements > 0 &&
                            currentTotals.engagements !== platformTotals.engagements ? (
                              <div className="text-sm">
                                <div>
                                  {platformTotals.engagements.toLocaleString()} /{' '}
                                  {currentTotals.engagements.toLocaleString()}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span
                                    className={
                                      platformTotals.engagements < currentTotals.engagements
                                        ? 'text-yellow-500'
                                        : 'text-red-500'
                                    }
                                  >
                                    (
                                    {Math.abs(
                                      currentTotals.engagements - platformTotals.engagements
                                    ).toLocaleString()}{' '}
                                    {platformTotals.engagements < currentTotals.engagements
                                      ? 'allocated'
                                      : 'over'}
                                    )
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => requestClearAllocated('engagements')}
                                    className={`rounded p-0.5 transition-colors hover:bg-white/10 ${
                                      darkMode
                                        ? 'text-gray-400 hover:text-gray-300'
                                        : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                    title="Clear allocated engagements"
                                  >
                                    <XCircleIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              currentTotals.engagements.toLocaleString()
                            )}
                          </div>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg CPM: {currencySymbol}
                            {platformTotals.engagements_cpm.toFixed(4)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Platform Cards */}
                    <div className="px-3 sm:px-6">
                      {enabledPlatformIds.length === 0 ? (
                        <div
                          className={`py-12 text-center ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          <p>No platforms enabled for this organization.</p>
                          <p className="mt-2 text-sm">
                            Go to Plugin Settings to enable social media platforms.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2 sm:space-y-4">
                          {enabledPlatformIds.map((platformId) => {
                            const platform = getPlatformById(platformId);
                            if (!platform) return null;

                            return (
                              <SocialPlatformCard
                                key={platformId}
                                platform={platform}
                                metrics={platformMetrics[platformId]}
                                onChange={(metrics) => handlePlatformChange(platformId, metrics)}
                                disabled={saving}
                                numberFormat={numberFormat}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4">
                <SecondaryButton onClick={onClose} disabled={saving}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  onClick={handleSave}
                  loading={saving}
                  disabled={loading || enabledPlatformIds.length === 0}
                >
                  Save Platform Data
                </PrimaryButton>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Confirmation Modal for Clearing Allocated Values */}
      <Dialog open={showClearConfirmation} onClose={cancelClearAllocated} className="relative z-50">
        <DialogBackdrop
          transition
          className={`fixed inset-0 backdrop-blur-[2px] transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in ${
            darkMode ? 'bg-gray-900/75' : 'bg-gray-500/75'
          }`}
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className={`relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              <div className="px-6 py-4">
                <div className="flex items-start">
                  <div
                    className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${
                      darkMode ? 'bg-yellow-500/10' : 'bg-yellow-100'
                    }`}
                  >
                    <ExclamationTriangleIcon
                      className="h-6 w-6 text-yellow-500"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-0 ml-4 flex-1">
                    <DialogTitle
                      className={`text-lg font-semibold ${
                        darkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}
                    >
                      Clear Allocated{' '}
                      {metricToClear
                        ? metricToClear.charAt(0).toUpperCase() + metricToClear.slice(1)
                        : 'Value'}
                      ?
                    </DialogTitle>
                    <div className="mt-2">
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Are you sure you want to clear the allocated {metricToClear} value from this
                        valuation? This will permanently remove the reference value and you will
                        start fresh with the platform breakdown.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4">
                <SecondaryButton onClick={cancelClearAllocated}>Cancel</SecondaryButton>
                <PrimaryButton onClick={confirmClearAllocated}>Clear Allocated Value</PrimaryButton>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}
